import {UseDocumentPreviewReturnType} from 'platform/document-preview';

import {useEffect, useMemo} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {getSafely} from 'shared/web';

import {
  DeliveryNoteDocumentPreviewFormDefinition,
  DeliveryNoteItem,
} from '../types/DeliveryNoteDocumentPreviewFormDefinition';

interface UseInvalidItemsCountParams {
  isConfirmed: boolean;
  pageNumber: number;
  listOfItems: DeliveryNoteDocumentPreviewFormDefinition['listOfItems'];
  documentPreview: UseDocumentPreviewReturnType;
}

interface InvalidItemsCountResult {
  invalidItemsCount: number;
  invalidItemsCountPerPage: number;
  invalidManufacturersCount: number;
}

export function useInvalidItemsCount(props: UseInvalidItemsCountParams): InvalidItemsCountResult {
  const result = useMemo(() => {
    if (!props.isConfirmed) {
      return {
        invalidItemsCount: 0,
        invalidItemsCountPerPage: 0,
        invalidManufacturersCount: 0,
        invalidCountByPage: {} as Record<number, number>,
      };
    }

    const {invalidItemsCount, invalidManufacturersCount, invalidCountByPage} = (
      props.listOfItems || []
    ).reduce(
      (acc, item) => {
        if (!item || !item.isSelected) {
          return acc;
        }

        const numericFields: (keyof DeliveryNoteItem)[] = ['quantity', 'unitPrice', 'totalPrice'];

        const invalidCount = Object.keys(item).reduce((count, key) => {
          if (
            key === 'isSelected' ||
            key === 'totalPriceWithVat' ||
            key === 'pageNumber' ||
            key === 'lastAutoFilledCatalogNumber'
          ) {
            return count;
          }

          const typedKey = key as keyof DeliveryNoteItem;

          const value = getSafely(item, typedKey);

          if (numericFields.includes(typedKey)) {
            if (!isNotNil(value) || value <= 0) {
              return count + 1;
            }
            return count;
          }

          if (!value) {
            return count + 1;
          }

          return count;
        }, 0);

        const hasEmptyManufacturer = !item.manufacturer;

        return {
          invalidItemsCount: acc.invalidItemsCount + invalidCount,
          invalidManufacturersCount: acc.invalidManufacturersCount + (hasEmptyManufacturer ? 1 : 0),
          invalidCountByPage: {
            ...acc.invalidCountByPage,
            [item.pageNumber]: (acc.invalidCountByPage[item.pageNumber] ?? 0) + invalidCount,
          },
        };
      },
      {
        invalidItemsCount: 0,
        invalidManufacturersCount: 0,
        invalidCountByPage: {} as Record<number, number>,
      }
    );

    return {
      invalidItemsCount,
      invalidItemsCountPerPage: invalidCountByPage[props.pageNumber] ?? 0,
      invalidManufacturersCount,
      invalidCountByPage,
    };
  }, [props.listOfItems, props.isConfirmed, props.pageNumber]);

  useEffect(() => {
    if (!props.isConfirmed) {
      return;
    }

    props.documentPreview.state.pages.forEach((page) => {
      const count = result.invalidCountByPage[page.pageNumber];

      if (isNil(count)) {
        props.documentPreview.controls.setPageValidation(page.pageNumber, 'default');
      } else {
        props.documentPreview.controls.setPageValidation(
          page.pageNumber,
          count === 0 ? 'success' : 'error'
        );
      }
    });
    // Removed props.documentPreview.state.pages because it caused infinite loop.
    // This reference is stable from first render, we can safely omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.invalidCountByPage, props.isConfirmed, props.documentPreview.controls]);

  return result;
}
